/**
 * useWalletConnect.js — Orchestration wagmi connect + flow SIWE complet.
 *
 * Flux SPEC §6.4 / §8.4 :
 *   1. L'utilisateur clique sur un wallet dans WalletPickerPanel
 *      → dispatch SELECT_WALLET(walletId) → état WALLET_CONNECTING
 *   2. useConnect (wagmi) connecte le wallet → address disponible
 *      → dispatch WALLET_CONNECTED(address)
 *   3. GET /auth/nonce?address= → message EIP-4361 COMPLET
 *   4. signMessageAsync({ message }) → signature (le message est signé TEL QUEL)
 *      → dispatch SIGNATURE_OBTAINED
 *   5. POST /auth/verify-signature { message, signature, address }
 *      - Wallet existant → dispatch VERIFY_SUCCESS_EXISTING(user)
 *      - Nouvelle adresse → dispatch VERIFY_SUCCESS_NEW
 *      - Erreur réseau / refus → dispatch CONNECT_ERROR / USER_REJECTED / NETWORK_MISMATCH
 *
 * Listener accountsChanged (§10.4 SPEC) :
 *   window.ethereum.on('accountsChanged', ...) → dispatch RESET quand le compte
 *   change depuis l'extension wallet (MetaMask). Nettoyé au unmount.
 *
 * Détection d'erreurs wagmi :
 *   - UserRejectedRequestError / code 4001 → USER_REJECTED
 *   - Chain ID mismatch → NETWORK_MISMATCH
 *   - Autres → CONNECT_ERROR
 *
 * §8.7 — aucun import Redux. Le parent reçoit onSuccess et dispatch.
 */

import { useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useConnect, useDisconnect, useSignMessage, useAccount } from 'wagmi';
import { ACTION_TYPES } from './useWalletStateMachine';
import { readStoredUser } from '../../../utils/auth/userStorage';
import { getNonce, verifySignature, linkWallet } from '../services/WalletAuthService';
import { resolvedChainId } from '../config/wagmiConfig';
import { privateKeyToAccount } from 'viem/accounts'; // signature SIWE locale (wallet de test dev/staging)

/* ─── Codes d'erreur wagmi courants ─────────────────────────────────────────── */
const USER_REJECTED_CODES = [4001, -32603];
const isUserRejection = (err) =>
  err?.name === 'UserRejectedRequestError' ||
  USER_REJECTED_CODES.includes(err?.code) ||
  /user rejected/i.test(err?.message ?? '');

const isNetworkMismatch = (err) =>
  err?.name === 'ChainMismatchError' ||
  /chain.*mismatch|wrong.*network|switch.*chain/i.test(err?.message ?? '');

/* ─── Reconnexion : fenêtre de session de sécurité (Jonathan 2026-06-10 : 5 min) ────────
   Si l'utilisateur s'est authentifié il y a < 5 min pour CETTE adresse, on réutilise la
   session au lieu de redemander une signature (pas de re-popup MetaMask). Au-delà → re-signe.
   Fail-safe : toute incertitude → null → flux de signature normal. */
const SESSION_TTL_MS = 5 * 60 * 1000;
function readValidSession(address) {
  try {
    if (!address) return null;
    const ts = Number(localStorage.getItem('_authTs') || 0);
    if (!ts || Date.now() - ts > SESSION_TTL_MS) return null;
    const u = readStoredUser();
    if (u && u.metamask && u.metamask.toLowerCase() === address.toLowerCase()) return u;
  } catch (e) { /* localStorage indispo / JSON cassé → flux normal */ }
  return null;
}

/**
 * useWalletConnect
 *
 * @param {object} params
 * @param {function} params.dispatch   - dispatch de useWalletStateMachine
 * @param {string}   params.apiBaseUrl - URL de base de l'API
 * @param {number}   params.chainId    - chainId attendu (ex: 43114)
 *
 * @returns {{
 *   connectWallet: function(wagmiConnectorId: string) => Promise<void>,
 *   cancelConnect: function() => void,
 *   isConnecting: boolean,
 * }}
 */
export function useWalletConnect({ dispatch, apiBaseUrl, chainId }) {
  const { connectAsync, connectors, isPending: isConnecting } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { address: wagmiAddress } = useAccount();

  /* Ref pour annuler le flow en cours (ex: clic "Annuler") */
  const cancelledRef = useRef(false);

  /* ── Listener accountsChanged — §10.4 ───────────────────────────────────── */
  useEffect(() => {
    if (typeof window === 'undefined' || !window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      /* Ne pas interrompre une connexion en cours (window.__earniWalletConnecting = true pendant
         connectWallet). MetaMask émet parfois accountsChanged pendant eth_requestAccounts ou
         pendant la popup de signature → sans ce guard le cancelledRef est posé à true, le flow
         s'arrête silencieusement après que l'utilisateur a signé et aucune session n'est créée. */
      if (window.__earniWalletConnecting) return;
      /* Mutex switch global (index.tsx) : si le handler global réauthentifie le nouveau compte
         (__earniLoginPending), ne pas RESET ni toucher aux drapeaux ici (il va recharger la page). */
      if (window.__earniLoginPending) return;
      /* Si le compte change depuis le wallet (hors connexion active), réinitialise la modal. */
      if (accounts.length === 0 || (wagmiAddress && accounts[0]?.toLowerCase() !== wagmiAddress.toLowerCase())) {
        cancelledRef.current = true;
        window.__earniWalletConnecting = false;
        dispatch({ type: ACTION_TYPES.RESET });
      }
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
    };
  }, [dispatch, wagmiAddress]);

  /* ── cancelConnect — interrompt le flow en cours ─────────────────────────── */
  const cancelConnect = useCallback(async () => {
    cancelledRef.current = true;
    window.__earniWalletConnecting = false;
    try {
      await disconnectAsync();
    } catch {
      /* Ignore les erreurs de déconnexion */
    }
    dispatch({ type: ACTION_TYPES.RESET });
  }, [disconnectAsync, dispatch]);

  /* ── dispatchVerifyResult — aiguillage commun après verify-signature (§9) ──── */
  const dispatchVerifyResult = useCallback((address, verifyResp) => {
    if (verifyResp?.action === 'REGISTER') {
      /* Nouvelle adresse — pas de compte. registrationToken transmis au panel d'inscription. */
      dispatch({ type: ACTION_TYPES.VERIFY_SUCCESS_NEW, payload: { address, registrationToken: verifyResp.registrationToken } });
    } else if (verifyResp?.accessToken) {
      dispatch({ type: ACTION_TYPES.VERIFY_SUCCESS_EXISTING, payload: verifyResp });
    } else {
      dispatch({ type: ACTION_TYPES.CONNECT_ERROR, payload: { message: 'Unexpected server response.' } });
    }
  }, [dispatch]);

  /* ── linkSignedWallet — liaison d'une adresse au compte courant (linkMode) ──
     Appelé après obtention de la signature SIWE quand on est en mode "Link a wallet".
     POST /auth/wallet/link → WALLET_LINKED (retour MANAGE) ou CONNECT_ERROR (ex: 409
     "address already linked to an account"). Ne se connecte PAS comme un nouveau compte. */
  const linkSignedWallet = useCallback(async ({ message, signature, address }) => {
    try {
      const resp = await linkWallet(apiBaseUrl, { message, signature, address });
      if (cancelledRef.current) return;
      // 401 silently handled by axiosWithToken (timeout modal) → resp is [] with no wallet field.
      // Do not dispatch false success in that case.
      if (!resp || !resp.wallet) return;
      dispatch({ type: ACTION_TYPES.WALLET_LINKED });
    } catch (err) {
      if (cancelledRef.current) return;
      // Échec de liaison d'un wallet au compte courant (op wallet sur action utilisateur).
      try {
        Sentry.withScope((scope) => {
          scope.setTag('flow', 'wallet_link');
          Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
        });
      } catch { /* ne jamais casser le flux de liaison */ }
      dispatch({
        type: ACTION_TYPES.CONNECT_ERROR,
        payload: { message: err?.message ?? 'Could not link this wallet.' },
      });
    }
  }, [apiBaseUrl, dispatch]);

  /* ── connectWallet — orchestration principale ────────────────────────────── */
  const connectWallet = useCallback(async (wagmiConnectorId, linkMode = false) => {
    /* Mutex SIWE à deux drapeaux (cf. index.tsx) : si le handler global réauthentifie déjà un
       switch de compte (__earniLoginPending), ne pas lancer une 2e SIWE concurrente — sinon
       2 GET /auth/nonce pour le même wallet → LoginNonce.upsert écrase le 1er → verify-signature
       renvoie 400 "invalid SIWE message format". Le handler global rechargera la page à la fin. */
    if (window.__earniLoginPending) return;
    cancelledRef.current = false;
    window.__earniWalletConnecting = true;

    /* Retrouver le connecteur wagmi par son id */
    const connector = connectors.find((c) => c.id === wagmiConnectorId);
    if (!connector) {
      dispatch({
        type: ACTION_TYPES.CONNECT_ERROR,
        payload: { message: `Connector "${wagmiConnectorId}" not found.` },
      });
      return;
    }

    let address;
    try {
      /* ── Étape 1 : connexion wagmi ───────────────────────────────────────── */
      let result;
      try {
        result = await connectAsync({ connector });
      } catch (e) {
        /* wagmi conserve une connexion active entre tentatives → "Connector already connected".
           On réutilise la connexion existante au lieu d'échouer (pas de re-popup wallet). */
        if (/already connected/i.test(e?.message || '') || e?.name === 'ConnectorAlreadyConnectedError') {
          const accounts = await connector.getAccounts();
          const cid = connector.getChainId ? await connector.getChainId() : undefined;
          result = { accounts, chainId: cid };
        } else {
          throw e;
        }
      }
      if (cancelledRef.current) return;

      address = result?.accounts?.[0];
      if (!address) throw new Error('No address returned by the wallet.');

      /* Vérification du chainId */
      const connectedChainId = result?.chainId;
      if (connectedChainId && connectedChainId !== (chainId ?? resolvedChainId)) {
        dispatch({
          type: ACTION_TYPES.NETWORK_MISMATCH,
        });
        return;
      }

      dispatch({
        type: ACTION_TYPES.WALLET_CONNECTED,
        payload: { address },
      });

      /* ── Reconnexion : session valide (<5 min, même adresse) → pas de re-signature ──
         On réutilise la session existante au lieu de rouvrir MetaMask pour signer.
         Hors linkMode (lier un wallet exige toujours une preuve de possession fraîche). */
      if (!linkMode) {
        const existing = readValidSession(address);
        if (existing) {
          dispatch({ type: ACTION_TYPES.VERIFY_SUCCESS_EXISTING, payload: existing });
          return;
        }
      }

      /* ── Étape 2 : récupération du nonce (message EIP-4361 COMPLET) ──────── */
      let message;
      try {
        const nonceResp = await getNonce(apiBaseUrl, address);
        if (cancelledRef.current) return;
        /* Le contrôleur retourne { nonce: message, message, expiresAt }
           Les deux champs contiennent le message complet à signer. */
        message = nonceResp?.message ?? nonceResp?.nonce;
        if (!message) throw new Error('SIWE message missing from the /auth/nonce response.');
      } catch (err) {
        if (cancelledRef.current) return;
        dispatch({
          type: ACTION_TYPES.CONNECT_ERROR,
          payload: { message: err?.message ?? 'Could not fetch the SIWE challenge.' },
        });
        return;
      }

      /* ── Étape 3 : signature du message EIP-4361 ─────────────────────────── */
      let signature;
      try {
        signature = await signMessageAsync({ message });
        if (cancelledRef.current) return;

        dispatch({ type: ACTION_TYPES.SIGNATURE_OBTAINED });
      } catch (err) {
        if (cancelledRef.current) return;
        if (isUserRejection(err)) {
          dispatch({ type: ACTION_TYPES.USER_REJECTED });
        } else {
          dispatch({
            type: ACTION_TYPES.CONNECT_ERROR,
            payload: { message: err?.message ?? 'The signature failed.' },
          });
        }
        return;
      }

      /* ── Étape 4 (linkMode) : liaison au compte courant, pas de re-connexion ── */
      if (linkMode) {
        await linkSignedWallet({ message, signature, address });
        return;
      }

      /* ── Étape 4 : vérification signature côté backend ───────────────────── */
      let verifyResp;
      try {
        verifyResp = await verifySignature(apiBaseUrl, { message, signature, address });
        if (cancelledRef.current) return;
      } catch (err) {
        if (cancelledRef.current) return;
        if (isNetworkMismatch(err)) {
          dispatch({ type: ACTION_TYPES.NETWORK_MISMATCH });
        } else {
          dispatch({
            type: ACTION_TYPES.CONNECT_ERROR,
            payload: { message: err?.message ?? 'Échec de la vérification de signature.' },
          });
        }
        return;
      }

      /* ── Étape 5 : dispatch selon la réponse backend ─────────────────────── */
      dispatchVerifyResult(address, verifyResp);
    } catch (err) {
      if (cancelledRef.current) return;
      if (isUserRejection(err)) {
        dispatch({ type: ACTION_TYPES.USER_REJECTED });
      } else if (isNetworkMismatch(err)) {
        dispatch({ type: ACTION_TYPES.NETWORK_MISMATCH });
      } else {
        // Anomalie réelle de connexion wallet (hors refus utilisateur / mauvais réseau, normaux).
        // Capture Sentry directe (pas un flow paiement) — défensive, ne propage jamais (§107).
        try {
          Sentry.withScope((scope) => {
            scope.setTag('flow', 'wallet_connect');
            Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
          });
        } catch { /* ne jamais casser le flux de connexion */ }
        dispatch({
          type: ACTION_TYPES.CONNECT_ERROR,
          payload: { message: err?.message ?? 'Wallet connection error.' },
        });
      }
    } finally {
      window.__earniWalletConnecting = false;
    }
  }, [connectAsync, connectors, signMessageAsync, dispatch, apiBaseUrl, chainId, dispatchVerifyResult, linkSignedWallet]);

  /* ── connectTestWallet — wallet de test dev/staging (signe le SIWE via viem, sans MetaMask) ──
     Activé uniquement si REACT_APP_TEST_SIGNER_KEY est présent (bakée build dev/staging, jamais prod). */
  const connectTestWallet = useCallback(async (linkMode = false) => {
    /* Mutex SIWE (cf. connectWallet) : pas de SIWE concurrente si un switch global est en cours. */
    if (window.__earniLoginPending) return;
    cancelledRef.current = false;
    window.__earniWalletConnecting = true;
    const key = process.env.REACT_APP_TEST_SIGNER_KEY;
    if (!key) {
      window.__earniWalletConnecting = false;
      dispatch({ type: ACTION_TYPES.CONNECT_ERROR, payload: { message: 'Test key missing (REACT_APP_TEST_SIGNER_KEY).' } });
      return;
    }
    try {
      const account = privateKeyToAccount(key.startsWith('0x') ? key : `0x${key}`);
      const address = account.address;
      if (cancelledRef.current) return;
      dispatch({ type: ACTION_TYPES.WALLET_CONNECTED, payload: { address } });

      const nonceResp = await getNonce(apiBaseUrl, address);
      if (cancelledRef.current) return;
      const message = nonceResp?.message ?? nonceResp?.nonce;
      if (!message) throw new Error('SIWE message missing.');

      const signature = await account.signMessage({ message });
      if (cancelledRef.current) return;
      dispatch({ type: ACTION_TYPES.SIGNATURE_OBTAINED });

      if (linkMode) {
        await linkSignedWallet({ message, signature, address });
        return;
      }

      const verifyResp = await verifySignature(apiBaseUrl, { message, signature, address });
      if (cancelledRef.current) return;
      dispatchVerifyResult(address, verifyResp);
    } catch (err) {
      if (cancelledRef.current) return;
      dispatch({ type: ACTION_TYPES.CONNECT_ERROR, payload: { message: err?.message ?? 'Test wallet failed.' } });
    } finally {
      window.__earniWalletConnecting = false;
    }
  }, [dispatch, apiBaseUrl, dispatchVerifyResult, linkSignedWallet]);

  return {
    connectWallet,
    connectTestWallet,
    cancelConnect,
    isConnecting,
  };
}

export default useWalletConnect;
